import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import type { Location } from 'react-router-dom'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { INBOX_TYPES } from '@disco/constants/GroupTypes'
import { PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  insertItem,
  insertPlaylist,
  insertSelectedItems,
} from '@disco/state-management/actions/ShortlistActions'
import {
  getGroup,
  getGroups,
  getTeamLibrary,
} from '@disco/state-management/selectors/groups'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import { getRoutingPreviousLocation } from '@disco/state-management/selectors/routing'
import { getAllSelectedItems } from '@disco/state-management/selectors/selection'
import { getShortlists } from '@disco/state-management/selectors/shortlists'
import { getTrack } from '@disco/state-management/selectors/tracks'
import {
  sendPlaylistDownloadEventThrottled,
  sendTrackDownloadEventThrottled,
} from '@disco/utils/eventsApi'
import { getModalBestReturnTo } from '@disco/utils/modals'

import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import type { Shortlist } from 'types/shortlist'
import type { Track } from 'types/track'

import DocumentHeadTags from 'components/document-head-tags'
import AddToShortlistModal from 'components/modals/add-to-shortlist'

import type { RootState } from 'store/configureStore'

function mapStateToProps(
  state: RootState,
  ownProps: {
    params: { trackId?: string; playlistId?: string }
    searchParams: URLSearchParams
  },
) {
  const track = ownProps.params.trackId
    ? getTrack(state, {
        id: parseInt(ownProps.params.trackId, 10),
      })
    : null
  const playlist = ownProps.params.playlistId
    ? getPlaylist(state, {
        id: parseInt(ownProps.params.playlistId, 10),
      })
    : null
  const groupParam = ownProps.searchParams.get('group')
  const group = groupParam
    ? getGroup(state, {
        groupId: parseInt(groupParam, 10),
      })
    : null
  const sourcePlaylistParam = ownProps.searchParams.get('source_playlist')
  const sourcePlaylistId = sourcePlaylistParam
    ? parseInt(sourcePlaylistParam, 10)
    : undefined
  return {
    shortlists: getShortlists(state),
    track: track,
    playlist: playlist,
    group: group,
    sourcePlaylistId: sourcePlaylistId,
    selectedItems: getAllSelectedItems(state),
    groups: getGroups(state),
    teamLibrary: getTeamLibrary(state),
    previousLocation: getRoutingPreviousLocation(state),
  }
}

const actions = {
  insertItem,
  insertPlaylist,
  insertSelectedItems,
  sendMixpanelEvent,
}
const connector = connect(mapStateToProps, actions)

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux

function AddToShortlistComponent(props: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const onClose = React.useCallback(() => {
    const returnTo = getModalBestReturnTo(location, props.previousLocation)
    navigate(returnTo)
  }, [location, props.previousLocation, navigate])

  const sendDownloadEvents = React.useCallback(
    (items: (Track | Playlist)[]) => {
      const { group } = props
      if (!group || !INBOX_TYPES.includes(group.group_type)) return
      items.forEach(item => {
        if (item.type === TRACK) {
          sendTrackDownloadEventThrottled(item.id, 'in-app', {
            group_id: group.id,
            playlist_id: props.sourcePlaylistId,
          })
        } else if (item.type === PLAYLIST) {
          sendPlaylistDownloadEventThrottled(
            item.playlist_id,
            undefined,
            group.id,
          )
        }
      })
    },
    [props.group, props.sourcePlaylistId],
  )

  const onAddToShortlist = React.useCallback(
    (shortlist: Shortlist) => {
      if (props.track) {
        props.insertItem(shortlist.id, {
          track: props.track,
        })
        props.sendMixpanelEvent('Add track to shortlist via add to shortlist')

        sendDownloadEvents([props.track])
      } else if (props.playlist) {
        props.insertPlaylist(shortlist.id, {
          playlist: props.playlist,
        })
        props.sendMixpanelEvent(
          'Add playlist to shortlist via add to shortlist',
        )

        sendDownloadEvents([props.playlist])
      } else {
        props.insertSelectedItems(shortlist.id, props.selectedItems)

        sendDownloadEvents(props.selectedItems)
      }

      onClose()
    },
    [
      props.track,
      props.playlist,
      props.selectedItems,
      props.insertItem,
      props.insertPlaylist,
      props.insertSelectedItems,
      props.sendMixpanelEvent,
      sendDownloadEvents,
      onClose,
    ],
  )

  const exclusionList = searchParams.get('exclude')
    ? [searchParams.get('exclude')]
    : undefined
  return (
    <div>
      <DocumentHeadTags title="Add to shortlist | DISCO" />
      <AddToShortlistModal
        shortlists={props.shortlists}
        onAddToShortlist={onAddToShortlist}
        exclusionList={exclusionList}
        onClose={onClose}
      />
    </div>
  )
}

const ConnectedAddToShortlist = connector(AddToShortlistComponent)

// Wrapper component to provide router params to the connected component
const AddToShortlistWithRouter = () => {
  const params = useParams<{ trackId?: string; playlistId?: string }>()
  const [searchParams] = useSearchParams()

  return <ConnectedAddToShortlist params={params} searchParams={searchParams} />
}

export default AddToShortlistWithRouter
